import { Vector3 } from 'three';
import {
  classifyToeParentName,
  getSemanticNamingPatterns,
} from '../schema/namingCatalog';

// ─── Utility ────────────────────────────────────────────────────────────

export function clamp(value: number, low = 0.0, high = 1.0): number {
  return Math.max(low, Math.min(high, value));
}

// ─── Score breakdown ────────────────────────────────────────────────────

export interface ScoreBreakdown {
  topology: number;
  symmetry: number;
  direction: number;
  lengthRatio: number;
  naming: number;
  depth: number;
  total: number;
}

export const WEIGHTS: Record<string, number> = {
  topology: 0.30,
  symmetry: 0.20,
  direction: 0.20,
  lengthRatio: 0.15,
  depth: 0.10,
  naming: 0.05,
};

// ─── Scoring functions ──────────────────────────────────────────────────

export function topologyScore(
  hasExpectedParent: boolean,
  expectedChildCount: [number, number],
  actualChildCount: number,
  isLinearChain: boolean,
): number {
  let score = 0.0;
  if (hasExpectedParent) score += 0.4;
  if (
    expectedChildCount[0] <= actualChildCount &&
    actualChildCount <= expectedChildCount[1]
  ) {
    score += 0.3;
  } else {
    score += Math.max(
      0.0,
      0.3 - 0.1 * Math.abs(actualChildCount - expectedChildCount[1]),
    );
  }
  if (isLinearChain) score += 0.3;
  return Math.min(score, 1.0);
}

export function symmetryScore(
  chainLenA: number,
  chainLenB: number,
  depthA: number,
  depthB: number,
  mirrorDot: number,
): number {
  const maxLen = Math.max(chainLenA, chainLenB);
  const lengthRatio =
    maxLen <= 1e-8 ? 0.0 : Math.min(chainLenA, chainLenB) / maxLen;
  const depthSc = 1.0 - Math.min(Math.abs(depthA - depthB) / 3.0, 1.0);
  const mirrorSc = Math.max(0.0, mirrorDot);
  return clamp(0.5 * lengthRatio + 0.25 * depthSc + 0.25 * mirrorSc);
}

export function directionScore(
  boneDir: Vector3,
  expectedDir: Vector3,
): number {
  const normA = boneDir.length();
  const normB = expectedDir.length();
  if (normA < 1e-8 || normB < 1e-8) return 0.0;
  const d = boneDir.dot(expectedDir) / (normA * normB);
  return clamp((d + 1.0) / 2.0);
}

export function lengthRatioScore(
  lenA: number,
  lenB: number,
  tolerance = 0.4,
): number {
  const maxLen = Math.max(lenA, lenB);
  if (maxLen <= 1e-8) return 0.0;
  const ratio = Math.min(lenA, lenB) / maxLen;
  return clamp((ratio - tolerance) / (1.0 - tolerance));
}

export function depthScore(
  actual: number,
  expectedRange: [number, number],
): number {
  if (expectedRange[0] <= actual && actual <= expectedRange[1]) return 1.0;
  const d = Math.min(
    Math.abs(actual - expectedRange[0]),
    Math.abs(actual - expectedRange[1]),
  );
  return clamp(1.0 - d / 4.0);
}

export function namingScore(name: string, hints: string[]): number {
  const lower = name.toLowerCase();
  for (const hint of hints) {
    if (lower.includes(hint)) return 1.0;
  }
  return 0.0;
}

export function totalScore(
  breakdown: ScoreBreakdown,
  weights: Record<string, number> = WEIGHTS,
): number {
  let sum = 0;
  for (const [k, w] of Object.entries(weights)) {
    const val = (breakdown as unknown as Record<string, number>)[k];
    if (val !== undefined) sum += val * w;
  }
  return sum;
}

export function confidenceTier(score: number): string {
  if (score >= 0.9) return 'very high';
  if (score >= 0.8) return 'high';
  if (score >= 0.65) return 'medium';
  return 'low';
}

// ─── Spatial scoring helpers ────────────────────────────────────────────

export function lateralSpreadScore(
  posA: Vector3,
  posB: Vector3,
  lateralAxis: Vector3,
  referencePos: Vector3,
  minSpread = 0.5,
): number {
  const tmp = new Vector3();
  const offsetA = tmp.subVectors(posA, referencePos).dot(lateralAxis);
  const offsetB = tmp.subVectors(posB, referencePos).dot(lateralAxis);

  if (offsetA * offsetB >= 0) return 0.3;

  const spread = Math.abs(offsetA - offsetB);
  if (spread < minSpread) return clamp(0.3 + 0.7 * (spread / minSpread));
  return 1.0;
}

export function centerAlignmentScore(
  position: Vector3,
  lateralAxis: Vector3,
  referencePos: Vector3,
  tolerance = 1.0,
): number {
  const offset = Math.abs(
    new Vector3().subVectors(position, referencePos).dot(lateralAxis),
  );
  if (offset <= tolerance) return 1.0;
  return clamp(1.0 - (offset - tolerance) / (tolerance * 3.0));
}

export function jointPrebendRatio(
  parentPos: Vector3,
  jointPos: Vector3,
  childPos: Vector3,
  lateralAxis: Vector3,
  downAxis: Vector3,
): number {
  const parentToChild = new Vector3().subVectors(childPos, parentPos);
  const lineLen = parentToChild.length();
  if (lineLen < 1e-6) return 0.0;

  const upAxis = downAxis.clone().negate();
  const forward = new Vector3().crossVectors(lateralAxis, upAxis);
  const fn = forward.length();
  if (fn < 1e-8) return 0.0;
  forward.divideScalar(fn);

  const lineDir = parentToChild.divideScalar(lineLen);
  const offset = new Vector3().subVectors(jointPos, parentPos);
  const projScalar = offset.dot(lineDir);
  const proj = parentPos.clone().addScaledVector(lineDir, projScalar);
  const displacement = new Vector3().subVectors(jointPos, proj);

  return displacement.dot(forward) / lineLen;
}

export function toeParentScore(parentName: string): number {
  switch (classifyToeParentName(parentName)) {
    case 'foot':
      return 1.0;
    case 'leg':
      return 0.6;
    case 'toe':
      return 0.3;
    default:
      return 0.5;
  }
}

export function toeDirectionAlignmentScore(
  toeDir: Vector3,
  legChainDir: Vector3,
  footDir: Vector3,
): number {
  const toeNorm = toeDir.length();
  const footNorm = footDir.length();
  const legNorm = legChainDir.length();

  if (toeNorm < 1e-8) return 0.5;

  const normToe = toeDir.clone().divideScalar(toeNorm);

  if (footNorm > 1e-8) {
    const normFoot = footDir.clone().divideScalar(footNorm);
    const footDot = normToe.dot(normFoot);
    if (footDot > 0.5) return 1.0;
    if (footDot > 0) return 0.7;
    if (footDot > -0.5) return 0.4;
    return 0.2;
  }

  if (legNorm > 1e-8) {
    const normLeg = legChainDir.clone().divideScalar(legNorm);
    const legDot = Math.abs(normToe.dot(normLeg));
    if (legDot < 0.3) return 1.0;
    if (legDot < 0.6) return 0.7;
    return 0.4;
  }

  return 0.5;
}

// ─── Naming mode ────────────────────────────────────────────────────────

export enum NamingMode {
  SEMANTIC = 'semantic',
  GENERIC = 'generic',
  MIXED = 'mixed',
}

// ─── Weight & scoring profiles ──────────────────────────────────────────

export interface WeightProfile {
  naming: number;
  namingHigh: number;
  topology: number;
  topologyHigh: number;
  direction: number;
  symmetry: number;
  chainPosition: number;
  constraint: number;
  constraintHigh: number;
  spatial: number;
  spatialHigh: number;
  spatialLimb: number;
  spatialRelative: number;
  sideMatch: number;
}

export interface ScoringProfile {
  mode: NamingMode;
  weights: WeightProfile;
  description: string;
  useSpatialSideFallback: boolean;
  minConfidence: number;
  trustSpatialPelvis: boolean;
}

export function scaleWeightCategory(
  profile: WeightProfile,
  category: string,
  factor: number,
): WeightProfile {
  const result = { ...profile };
  for (const key of Object.keys(result) as (keyof WeightProfile)[]) {
    if (key.toLowerCase().startsWith(category.toLowerCase())) {
      result[key] = result[key] * factor;
    }
  }
  return result;
}

export function getWeight(profile: ScoringProfile, weightName: string): number {
  return (
    (profile.weights as unknown as Record<string, number>)[weightName] ?? 0.0
  );
}

// ─── Preset weight profiles ─────────────────────────────────────────────

const SEMANTIC_WEIGHTS: WeightProfile = {
  naming: 0.1,
  namingHigh: 0.2,
  topology: 0.15,
  topologyHigh: 0.25,
  direction: 0.25,
  symmetry: 0.15,
  chainPosition: 0.1,
  constraint: 0.1,
  constraintHigh: 0.2,
  spatial: 0.2,
  spatialHigh: 0.3,
  spatialLimb: 0.1,
  spatialRelative: 0.15,
  sideMatch: 0.5,
};

const GENERIC_WEIGHTS: WeightProfile = {
  naming: 0.02,
  namingHigh: 0.05,
  topology: 0.2,
  topologyHigh: 0.3,
  direction: 0.3,
  symmetry: 0.2,
  chainPosition: 0.15,
  constraint: 0.15,
  constraintHigh: 0.25,
  spatial: 0.35,
  spatialHigh: 0.45,
  spatialLimb: 0.2,
  spatialRelative: 0.25,
  sideMatch: 0.3,
};

const MIXED_WEIGHTS: WeightProfile = {
  naming: 0.06,
  namingHigh: 0.12,
  topology: 0.18,
  topologyHigh: 0.28,
  direction: 0.28,
  symmetry: 0.18,
  chainPosition: 0.12,
  constraint: 0.12,
  constraintHigh: 0.22,
  spatial: 0.28,
  spatialHigh: 0.38,
  spatialLimb: 0.15,
  spatialRelative: 0.2,
  sideMatch: 0.4,
};

// ─── Preset scoring profiles ────────────────────────────────────────────

export const SEMANTIC_PROFILE: ScoringProfile = {
  mode: NamingMode.SEMANTIC,
  weights: SEMANTIC_WEIGHTS,
  description:
    'Standard skeleton with semantic bone names (spine, arm, leg, etc.)',
  useSpatialSideFallback: false,
  minConfidence: 0.55,
  trustSpatialPelvis: false,
};

export const GENERIC_PROFILE: ScoringProfile = {
  mode: NamingMode.GENERIC,
  weights: GENERIC_WEIGHTS,
  description:
    'Skeleton with generic bone names (bone_0, bone_1, Joint1, etc.)',
  useSpatialSideFallback: true,
  minConfidence: 0.5,
  trustSpatialPelvis: true,
};

export const MIXED_PROFILE: ScoringProfile = {
  mode: NamingMode.MIXED,
  weights: MIXED_WEIGHTS,
  description: 'Skeleton with mixed naming (some semantic, some generic)',
  useSpatialSideFallback: true,
  minConfidence: 0.52,
  trustSpatialPelvis: true,
};

// ─── Naming detection ───────────────────────────────────────────────────

const SEMANTIC_PATTERNS = getSemanticNamingPatterns();

const GENERIC_PATTERNS: RegExp[] = [
  /^bone[_-]?\d+$/i,
  /^joint[_-]?\d+$/i,
  /^node[_-]?\d+$/i,
  /^obj[_-]?\d+$/i,
  /^bip\d+\s*[_-]?\w*$/i,
  /^\d+$/,
];

function isSemanticName(name: string): boolean {
  const lower = name.toLowerCase();
  return SEMANTIC_PATTERNS.some((p) => lower.includes(p));
}

function isGenericName(name: string): boolean {
  return GENERIC_PATTERNS.some((p) => p.test(name));
}

export function analyzeNamingDistribution(
  boneNames: string[],
  sampleSize = 30,
): [number, number, number] {
  const names =
    boneNames.length > sampleSize ? boneNames.slice(0, sampleSize) : boneNames;
  let semantic = 0;
  let generic = 0;
  let neutral = 0;

  for (const name of names) {
    if (isSemanticName(name)) semantic++;
    else if (isGenericName(name)) generic++;
    else neutral++;
  }

  return [semantic, generic, neutral];
}

export function detectNamingMode(
  boneNames: string[],
  semanticThreshold = 0.25,
  genericThreshold = 0.5,
): NamingMode {
  if (boneNames.length === 0) return NamingMode.SEMANTIC;

  const [semantic, generic, neutral] = analyzeNamingDistribution(boneNames);
  const total = semantic + generic + neutral;
  if (total === 0) return NamingMode.SEMANTIC;

  const semanticRatio = semantic / total;
  const genericRatio = generic / total;

  if (genericRatio >= genericThreshold) return NamingMode.GENERIC;
  if (semanticRatio >= semanticThreshold) return NamingMode.SEMANTIC;
  return NamingMode.MIXED;
}

export function getProfileForMode(mode: NamingMode): ScoringProfile {
  if (mode === NamingMode.GENERIC) return GENERIC_PROFILE;
  if (mode === NamingMode.MIXED) return MIXED_PROFILE;
  return SEMANTIC_PROFILE;
}
